import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
  SheetDescription,
} from "@/components/ui/sheet";
import { ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";

// Friendly action labels
const ACTION_LABELS = {
  "salary_batch.create":                   "Batch created",
  "salary_batch.submit":                   "Submitted to Finance",
  "salary_batch.finance_review.approve_all":   "Finance · approved all",
  "salary_batch.finance_review.reject_all":    "Finance · rejected all",
  "salary_batch.finance_review.approve_selected":"Finance · approved selected",
  "salary_batch.finance_review.reject_selected": "Finance · rejected selected",
  "salary_batch.final.approve":            "Admin · final approval",
  "salary_batch.final.reject":             "Admin · rejected",
  "salary_batch.final.return":             "Admin · returned to finance",
  "salary_batch.paid":                     "Marked paid / disbursed",
  "salary_batch.close":                    "Batch closed",
  "report.create":   "Report created",
  "report.submit":   "Report submitted",
  "report.approved": "Report approved",
  "report.rejected": "Report rejected",
  "report.returned": "Report returned",
};

const STATUS_COLOURS = {
  draft:                   "bg-slate-100 text-slate-700",
  submitted:               "bg-blue-100 text-blue-700",
  under_finance_review:    "bg-amber-100 text-amber-800",
  finance_cleared:         "bg-emerald-100 text-emerald-800",
  partially_cleared:       "bg-amber-100 text-amber-800",
  rejected_by_finance:     "bg-red-100 text-red-700",
  pending_final_approval:  "bg-violet-100 text-violet-800",
  approved:                "bg-emerald-100 text-emerald-800",
  partially_approved:      "bg-amber-100 text-amber-800",
  rejected:                "bg-red-100 text-red-700",
  paid_disbursed:          "bg-emerald-200 text-emerald-900",
  closed:                  "bg-slate-200 text-slate-700",
};

function labelize(s) {
  return s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";
}

export default function AuditTrailDrawer({ entity, entityId, trigger, label }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/audit-logs/trail/${entity}/${entityId}`)
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open, entity, entityId]);

  const defaultTrigger = (
    <Button size="sm" variant="outline" data-testid={`audit-trigger-${entityId}`}>
      <ClipboardText size={14} className="mr-1.5" /> Internal Audit
    </Button>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger || defaultTrigger}</SheetTrigger>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <ClipboardText size={18} /> Internal Audit
          </SheetTitle>
          <SheetDescription>
            {label || labelize(entity)} · chronological actions with user, role and timestamp.
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="mt-6 text-sm text-slate-500">Loading audit trail…</div>
        ) : items.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
            No audit entries on this record yet.
          </div>
        ) : (
          <ol className="mt-5 relative border-l-2 border-slate-200 pl-5 space-y-5">
            {items.map((e, i) => {
              const lbl = ACTION_LABELS[e.action] || labelize(e.action);
              const statusCls = STATUS_COLOURS[e.status_after] || "bg-slate-100 text-slate-700";
              return (
                <li key={e.id || i} className="relative" data-testid={`audit-entry-${i}`}>
                  <span className="absolute -left-[1.7rem] top-1 flex h-3 w-3 rounded-full bg-blue-500 ring-4 ring-white" />
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="font-semibold text-slate-900">{lbl}</div>
                    {e.status_after && (
                      <span className={`rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider ${statusCls}`}>
                        {labelize(e.status_after)}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-600">
                    <span className="font-semibold text-slate-900">{e.user_full_name || e.user_email || "Unknown"}</span>
                    <span className="mx-1 text-slate-400">·</span>
                    <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-indigo-800 font-mono-alt text-[10px] uppercase tracking-wider">
                      {e.user_role_label || labelize(e.user_role || "")}
                    </span>
                    {e.user_designation && (
                      <span className="ml-1.5 text-[11px] text-slate-500">· {e.user_designation}</span>
                    )}
                  </div>
                  <div className="mt-0.5 font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
                    {formatDateTime(e.created_at)}
                  </div>
                  {e.comment && (
                    <div className="mt-1.5 rounded bg-slate-50 px-2 py-1.5 text-xs text-slate-700">
                      “{e.comment}”
                    </div>
                  )}
                  {Array.isArray(e.changes) && e.changes.length > 0 && (
                    <ul className="mt-1.5 space-y-1 text-[11px]">
                      {e.changes.map((c, j) => (
                        <li key={j} className="rounded bg-amber-50 px-2 py-1 text-amber-900">
                          <span className="font-mono-alt uppercase tracking-wider text-[10px]">{c.field}</span>
                          <span className="ml-1 text-slate-400">{String(c.from || "—")}</span>
                          <span className="mx-1">→</span>
                          <span className="font-semibold">{String(c.to || "—")}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {e.details && Object.keys(e.details).length > 0 && (
                    <details className="mt-1.5 text-[11px]">
                      <summary className="cursor-pointer text-slate-500">details</summary>
                      <pre className="mt-1 rounded bg-slate-50 p-2 text-[10px] text-slate-700 whitespace-pre-wrap">
                        {JSON.stringify(e.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </SheetContent>
    </Sheet>
  );
}
